import { calculatePolygonCentroid } from 'components/BaseMap/util/BaseMap.utils';
import useDebounce from 'hooks/useDebounce';
import useLocation from 'hooks/useLocation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  useGetJurisdictionsQuery,
  useLazyGetJurisdictionByPlaceIdQuery,
} from 'services/geography/geography.api';
import {
  autoCompleteSearch,
  getCityStateFromCoordinates,
} from 'services/location/location.service';
import { stringIsCoordinates } from 'utils/maps/geometry.utils';
import {
  selectActiveAlertDraft,
  selectAlertMode,
  selectGetMapCenter,
} from '../../state/selectors/alertCreatorSelectors';
import {
  applyIpawsResolvedJurisdiction,
  clearIpawsResolvedJurisdiction,
  setMapCenter,
  updateActiveAlertDraft,
} from '../../state/slices/alertCreatorSlice';
import { ALERT_MODES } from '../../utils/alertCreator.utils';
import {
  describeResolvedJurisdiction,
  getIpawsAreaReferenceCoordinates,
  getIpawsSameGeocode,
} from '../../utils/ipawsArea.utils';

const getLocationLabel = (location) =>
  location?.description ?? location?.title ?? location?.name ?? '';

const normalizeAutocompleteResults = (response) => {
  const responseData = response?.data;
  const result = responseData?.result;
  const candidates = Array.isArray(result)
    ? result
    : (result?.predictions ?? responseData?.predictions ?? []);

  return candidates
    .map((location) => ({
      ...location,
      description: getLocationLabel(location),
      place_id: location.place_id ?? location.placeId ?? location.id,
    }))
    .filter((location) => location.description && location.place_id);
};

const logAutocompleteDiagnostics = ({ response, googleResults, jurisdictionResults }) => {
  if (process.env.NODE_ENV === 'production') return;

  const result = response?.data?.result;
  const candidates = Array.isArray(result)
    ? result
    : (result?.predictions ?? response?.data?.predictions);

  console.info('[IPAWS geography] Autocomplete diagnostics', {
    responseKeys: Object.keys(response?.data ?? {}),
    resultType: Array.isArray(result) ? 'array' : typeof result,
    candidateCount: Array.isArray(candidates) ? candidates.length : 0,
    firstCandidateKeys: Object.keys(candidates?.[0] ?? {}),
    normalizedGoogleCount: googleResults.length,
    matchingJurisdictionCount: jurisdictionResults.length,
  });
};

const getMatchingJurisdictionResults = (jurisdictions, searchTerm) => {
  const normalizedSearchTerm = searchTerm.trim().toLocaleLowerCase();
  if (!normalizedSearchTerm) return [];

  return jurisdictions
    .filter((jurisdiction) => {
      const name = jurisdiction.NAME ?? jurisdiction.name ?? '';
      return name.toLocaleLowerCase().includes(normalizedSearchTerm);
    })
    .slice(0, 10)
    .map((jurisdiction) => {
      const name = jurisdiction.NAME ?? jurisdiction.name;
      return {
        description: name,
        place_id: `jurisdiction-${jurisdiction.GEOID}`,
        jurisdiction: { ...jurisdiction, name },
      };
    });
};

/** Why a jurisdiction cannot be the IPAWS target: only a county has a SAME code. */
const getJurisdictionNotice = (jurisdiction) =>
  `${jurisdiction?.name ?? jurisdiction?.NAME ?? 'That place'} has no county SAME code. ` +
  'IPAWS targets counties: search for and select the county instead.';

/**
 * Searches for a place and keeps the map and alert's city/state details in sync.
 * For IPAWS, a county pick (from the workplace's own jurisdictions, or resolved from a Google
 * place) becomes the SAME geocode target; its boundary is drawn for reference only.
 */
const AlertLocationSearch = ({ widget }) => {
  const alertData = useSelector(selectActiveAlertDraft);
  const alertMode = useSelector(selectAlertMode);
  const getMapCenter = useSelector(selectGetMapCenter);
  const { locations } = useLocation();
  const workplaceLocation = locations.workplaceLocation;
  const workplaceLatitude = workplaceLocation.coords[0];
  const workplaceLongitude = workplaceLocation.coords[1];
  const dispatch = useDispatch();
  const [relativeLocation, setRelativeLocation] = useState({ lat: 0, lng: 0 });
  const [autoCompleteData, setAutoCompleteData] = useState([]);
  const [autocompleteStatus, setAutocompleteStatus] = useState('idle');
  const [jurisdictionNotice, setJurisdictionNotice] = useState('');
  const [query, setQuery] = useState('');
  const locationRequestIdRef = useRef(0);
  const autocompleteRequestIdRef = useRef(0);
  const jurisdictionRequestIdRef = useRef(0);
  const selectedLocationDescriptionRef = useRef('');
  const containerRef = useRef(null);
  const debouncedSearchTerm = useDebounce(query, 1000);
  const isIpaws = alertMode === ALERT_MODES.IPAWS;
  const { data: jurisdictionData } = useGetJurisdictionsQuery();
  const jurisdictions = useMemo(
    () => (Array.isArray(jurisdictionData) ? jurisdictionData : []),
    [jurisdictionData],
  );
  const [getJurisdictionByPlaceId, jurisdictionLookup] = useLazyGetJurisdictionByPlaceIdQuery();

  const selectSearchLocation = useCallback(
    async (location) => {
      // A search can still be in flight when a row is picked. Invalidate it before closing the
      // list so a late response cannot reopen the suggestions.
      autocompleteRequestIdRef.current += 1;
      setAutoCompleteData([]);
      setAutocompleteStatus('idle');
      selectedLocationDescriptionRef.current = location.description ?? '';
      setQuery(location.description ?? '');

      // A county is the target when it has a SAME code; a 7-digit place GEOID has none.
      const resolveJurisdiction = (jurisdiction) => {
        const sameGeocode = getIpawsSameGeocode(jurisdiction);
        if (!sameGeocode) {
          setJurisdictionNotice(getJurisdictionNotice(jurisdiction));
          return;
        }
        setJurisdictionNotice('');
        dispatch(
          applyIpawsResolvedJurisdiction({
            jurisdiction: describeResolvedJurisdiction(jurisdiction),
            sameGeocode,
          }),
        );
      };

      if (location.jurisdiction) {
        // Jurisdiction rows are offered in IPAWS mode only; a stale row cannot act elsewhere.
        if (!isIpaws) return;
        // A place lookup still in flight must not land on top of this pick.
        jurisdictionRequestIdRef.current += 1;
        const resolved = describeResolvedJurisdiction(location.jurisdiction);
        const center = calculatePolygonCentroid(resolved.boundaryRings[0] ?? []);
        dispatch(
          updateActiveAlertDraft({
            searchLocation: {
              description: location.description,
              place_id: location.place_id,
              latitude: center?.lat,
              longitude: center?.lng,
            },
          }),
        );
        dispatch(clearIpawsResolvedJurisdiction());
        resolveJurisdiction(location.jurisdiction);
        return;
      }

      dispatch(updateActiveAlertDraft({ searchLocation: location }));
      if (!isIpaws || !location?.place_id) return;

      const requestId = ++jurisdictionRequestIdRef.current;
      dispatch(clearIpawsResolvedJurisdiction());
      setJurisdictionNotice('');
      try {
        const jurisdiction = await getJurisdictionByPlaceId(location.place_id).unwrap();
        if (process.env.NODE_ENV !== 'production') {
          console.info('[IPAWS geography] Jurisdiction lookup diagnostics', {
            responseKeys: Object.keys(jurisdiction ?? {}),
            hasGeoid: Boolean(jurisdiction?.GEOID),
            hasBoundary: Boolean(jurisdiction?.original_coordinates),
            lookupType: jurisdiction?.lookup_type ?? null,
          });
        }
        if (requestId !== jurisdictionRequestIdRef.current || !jurisdiction) return;
        resolveJurisdiction(jurisdiction);
      } catch {
        // RTK Query exposes the error below; keep the selected place so the operator can retry.
      }
    },
    [dispatch, getJurisdictionByPlaceId, isIpaws],
  );

  useEffect(() => {
    setQuery(alertData.searchLocation?.description ?? '');
  }, [alertData.searchLocation, alertMode]);

  const affectedAreaCenter = useMemo(() => {
    // Use one point per IPAWS shape so polygons with many corners do not outweigh other shapes.
    const referenceCoordinates =
      alertMode === ALERT_MODES.IPAWS
        ? getIpawsAreaReferenceCoordinates(alertData.areaShapes)
        : alertData.coordinates;

    return calculatePolygonCentroid(referenceCoordinates);
  }, [alertData.areaShapes, alertData.coordinates, alertMode]);

  const searchAutoFill = useCallback(
    async (query) => {
      const searchLocation = query.trim();
      if (searchLocation === '') {
        setAutoCompleteData((currentResults) => (currentResults.length ? [] : currentResults));
        setAutocompleteStatus('idle');
        return [];
      }

      const requestId = ++autocompleteRequestIdRef.current;
      setAutocompleteStatus('loading');
      // The workplace's own counties are offered as targets in IPAWS mode only.
      const jurisdictionResults = isIpaws
        ? getMatchingJurisdictionResults(jurisdictions, searchLocation)
        : [];
      try {
        const response = await autoCompleteSearch(
          relativeLocation.lat,
          relativeLocation.lng,
          searchLocation,
        );
        if (requestId !== autocompleteRequestIdRef.current) return [];

        const googleResults = normalizeAutocompleteResults(response);
        logAutocompleteDiagnostics({ response, googleResults, jurisdictionResults });
        const jurisdictionGeoids = new Set(
          jurisdictionResults.map(({ jurisdiction }) => String(jurisdiction.GEOID)),
        );
        const results = [
          ...jurisdictionResults,
          ...googleResults.filter(
            (location) => !jurisdictionGeoids.has(String(location.GEOID ?? '')),
          ),
        ];
        setAutoCompleteData(results);
        setAutocompleteStatus(results.length ? 'success' : 'empty');
        return results;
      } catch (error) {
        if (requestId !== autocompleteRequestIdRef.current) return [];
        console.error('Error fetching autocomplete data:', error);
        setAutoCompleteData(jurisdictionResults);
        setAutocompleteStatus(jurisdictionResults.length ? 'success' : 'error');
        return jurisdictionResults;
      }
    },
    [isIpaws, jurisdictions, relativeLocation],
  );

  useEffect(() => {
    (async () => {
      if (debouncedSearchTerm === '' || stringIsCoordinates(debouncedSearchTerm)) {
        setAutoCompleteData((currentResults) => (currentResults.length ? [] : currentResults));
        setAutocompleteStatus('idle');
        return;
      }

      const alreadySelectedLocation =
        alertData.searchLocation?.description === debouncedSearchTerm ||
        selectedLocationDescriptionRef.current === alertData.searchLocation?.description;

      if (alreadySelectedLocation) {
        return;
      }

      await searchAutoFill(debouncedSearchTerm);
    })();
  }, [debouncedSearchTerm, searchAutoFill, alertData.searchLocation]);

  useEffect(() => {
    if (!affectedAreaCenter) {
      dispatch(updateActiveAlertDraft({ city: '', state: '', foundLocation: null }));
      return;
    }

    const requestId = ++locationRequestIdRef.current;
    let canceled = false;

    const updateLocationFromCoordinates = async () => {
      const res = await getCityStateFromCoordinates(affectedAreaCenter.lat, affectedAreaCenter.lng);

      if (canceled || requestId !== locationRequestIdRef.current) return;

      const result = res?.data?.result;
      if (!result) return;

      dispatch(
        updateActiveAlertDraft({
          state: result.state_name,
          city: result.city_name,
          foundLocation: [result.city_name, result.state_name].filter(Boolean).join(', '),
        }),
      );
    };

    updateLocationFromCoordinates();

    return () => {
      canceled = true;
    };
  }, [affectedAreaCenter, dispatch]);

  useEffect(() => {
    if (!alertData.state || !alertData.city) {
      dispatch(updateActiveAlertDraft({ foundLocation: null }));
    }
  }, [alertData.state, alertData.city, dispatch]);

  useEffect(() => {
    if (!alertData.searchLocation) return;

    dispatch(
      setMapCenter({
        lat: alertData.searchLocation?.latitude,
        lng: alertData.searchLocation?.longitude,
      }),
    );

    setRelativeLocation({
      lat: alertData.searchLocation?.latitude,
      lng: alertData.searchLocation?.longitude,
    });

    if (!alertData.foundLocation) {
      dispatch(
        updateActiveAlertDraft({
          state: alertData.searchLocation?.state_long,
          city: alertData.searchLocation?.city,
          foundLocation:
            alertData.searchLocation?.city + ', ' + alertData.searchLocation?.state_long,
        }),
      );
    }
  }, [alertData.searchLocation, alertData.foundLocation, dispatch]);

  useEffect(() => {
    setRelativeLocation((currentLocation) =>
      currentLocation.lat === getMapCenter.lat && currentLocation.lng === getMapCenter.lng
        ? currentLocation
        : { lat: getMapCenter.lat, lng: getMapCenter.lng },
    );
  }, [getMapCenter.lat, getMapCenter.lng]);

  // Bias the location search toward the workplace location the alert map defaults to
  useEffect(() => {
    setRelativeLocation((currentLocation) =>
      currentLocation.lat === workplaceLatitude && currentLocation.lng === workplaceLongitude
        ? currentLocation
        : { lat: workplaceLatitude, lng: workplaceLongitude },
    );
  }, [workplaceLatitude, workplaceLongitude]);

  // A pointer or scroll anywhere outside the search group dismisses the suggestions. The scroll
  // listener captures so it sees every scroll container on the page, which includes the list
  // itself (it is height-capped and scrolls), so scrolls that start inside the group are ignored.
  useEffect(() => {
    if (!autoCompleteData.length) return undefined;

    const closeSuggestions = () => {
      // A search can still be in flight when the list is dismissed. Invalidate it so a late
      // response cannot reopen the suggestions.
      autocompleteRequestIdRef.current += 1;
      setAutoCompleteData([]);
      setAutocompleteStatus('idle');
    };

    // A document or window scroll has a non-element target, which counts as outside.
    const isInsideSearch = (target) =>
      target instanceof Node && Boolean(containerRef.current?.contains(target));

    const handlePointerDown = (event) => {
      if (!isInsideSearch(event.target)) closeSuggestions();
    };

    const handleScroll = (event) => {
      if (!isInsideSearch(event.target)) closeSuggestions();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [autoCompleteData.length]);

  return (
    <Form.Group ref={containerRef} className="position-relative w-100">
      <Form.Label htmlFor="alert-creator-location-search" className="visually-hidden">
        Search for the affected location
      </Form.Label>
      <div className="alert-location-search">
        <Form.Control
          id="alert-creator-location-search"
          className="alert-creator-control w-100"
          type="search"
          name="searchLocation"
          value={query}
          autoComplete="off"
          aria-autocomplete="list"
          aria-controls="alert-location-suggestions"
          aria-expanded={autoCompleteData.length > 0}
          aria-describedby="alert-location-search-status"
          onChange={(e) => {
            selectedLocationDescriptionRef.current = '';
            setQuery(e.target.value);
          }}
          placeholder="Search Location"
        />

        {!widget && (
          <Button
            type="button"
            variant="secondary"
            className="alert-location-search-button"
            onClick={() => void searchAutoFill(query)}
          >
            Search
          </Button>
        )}
      </div>

      {autoCompleteData.length > 0 ? (
        <div
          id="alert-location-suggestions"
          className="alert-location-suggestions list-group position-absolute start-0 w-100"
          role="listbox"
          aria-label="Location suggestions"
        >
          {autoCompleteData.map((location, index) => (
            <button
              key={`${location.place_id}-${index}`}
              className="list-group-item list-group-item-action text-start"
              type="button"
              role="option"
              aria-selected="false"
              onClick={() => void selectSearchLocation(location)}
            >
              {location.description}
            </button>
          ))}
        </div>
      ) : null}

      <small id="alert-location-search-status" className="d-block mt-1" aria-live="polite">
        {autocompleteStatus === 'loading' ? 'Searching for locations…' : null}
        {autocompleteStatus === 'empty' ? 'No matching locations were found.' : null}
        {autocompleteStatus === 'error'
          ? 'Location search is unavailable. Try again or contact an administrator.'
          : null}
      </small>

      {isIpaws && jurisdictionLookup.isFetching ? (
        <small role="status">Resolving jurisdiction…</small>
      ) : null}
      {isIpaws && jurisdictionLookup.isError ? (
        <small role="alert">No jurisdiction boundary was found for that place.</small>
      ) : null}
      {isIpaws && jurisdictionNotice ? (
        <small role="alert" className="d-block mt-1">
          {jurisdictionNotice}
        </small>
      ) : null}
    </Form.Group>
  );
};

export default AlertLocationSearch;
